import { devLog, devError } from '../utils/devLogger';
import {
  collection,
  doc,
  addDoc,
  getDocs,
  getDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  limit,
  arrayUnion
} from 'firebase/firestore';
import { db, auth } from '../firebase';

/**
 * Enhanced Facilitator Service - Production-ready Firebase operations
 * Comprehensive error handling, validation, and full collection support
 * Implements the complete FacilitatorCollections.md structure
 */

// =========================
// VALIDATION SCHEMAS
// =========================

const ValidationSchemas = {
  module: {
    moduleName: { required: true, minLength: 3, maxLength: 100, type: 'string' },
    description: { required: true, minLength: 10, maxLength: 2000, type: 'string' },
    programId: { required: true, type: 'string' },
    startDate: { required: true, type: 'date' },
    endDate: { required: true, type: 'date' },
    difficulty: {
      required: true,
      enum: [
        'Incube Level 1', 'Incube Level 2', 'Incube Level 3', 'Incube Level 4', 'Incube Level 5',
        'Incube Level 6', 'Incube Level 7', 'Incube Level 8', 'Incube Level 9', 'Incube Level 10'
      ],
      type: 'string'
    },
    estimatedHours: { required: true, min: 1, max: 500, type: 'number' },
    learningObjectives: { required: true, minLength: 1, type: 'array' }
  },
  
  assignment: {
    title: { required: true, minLength: 3, maxLength: 200, type: 'string' },
    description: { required: false, maxLength: 2000, type: 'string' },
    moduleId: { required: true, type: 'string' },
    dueDate: { required: true, type: 'date' },
    maxScore: { required: true, min: 1, max: 1000, type: 'number' },
    type: { required: true, enum: ['assignment', 'project', 'assessment', 'portfolio', 'presentation'], type: 'string' }
  },
  
  cohort: {
    name: { required: true, minLength: 3, maxLength: 100, type: 'string' },
    programId: { required: true, type: 'string' },
    startDate: { required: true, type: 'date' },
    endDate: { required: true, type: 'date' },
    maxLearners: { required: true, min: 1, max: 100, type: 'number' }
  },
  
  progressReport: {
    cohortId: { required: true, type: 'string' },
    sessionDate: { required: true, type: 'date' },
    attendance: { required: true, type: 'object' },
    highlights: { required: false, maxLength: 1000, type: 'string' }
  }
};

// =========================
// ERROR HANDLING
// =========================

class FacilitatorServiceError extends Error {
  constructor(message, code, details = null) {
    super(message);
    this.name = 'FacilitatorServiceError';
    this.code = code;
    this.details = details;
    this.timestamp = new Date().toISOString();
  }
}

const ErrorCodes = {
  // Authentication Errors
  AUTH_REQUIRED: 'AUTH_REQUIRED',
  INSUFFICIENT_PERMISSIONS: 'INSUFFICIENT_PERMISSIONS',
  
  // Validation Errors
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  INVALID_INPUT: 'INVALID_INPUT',
  REQUIRED_FIELD_MISSING: 'REQUIRED_FIELD_MISSING',
  
  // Data Errors
  RESOURCE_NOT_FOUND: 'RESOURCE_NOT_FOUND',
  RESOURCE_ALREADY_EXISTS: 'RESOURCE_ALREADY_EXISTS',
  DATA_INTEGRITY_ERROR: 'DATA_INTEGRITY_ERROR',
  
  // System Errors
  FIREBASE_ERROR: 'FIREBASE_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  
  // Business Logic Errors
  COHORT_FULL: 'COHORT_FULL',
  ASSIGNMENT_PAST_DUE: 'ASSIGNMENT_PAST_DUE',
  MODULE_NOT_PUBLISHED: 'MODULE_NOT_PUBLISHED'
};

// =========================
// UTILITY FUNCTIONS
// =========================

class ValidationUtils {
  static validate(data, schema, path = '') {
    const errors = [];
    
    for (const [field, rules] of Object.entries(schema)) {
      const fieldPath = path ? `${path}.${field}` : field;
      const value = data[field];
      
      // Required field check
      if (rules.required && (value === undefined || value === null || value === '')) {
        errors.push({
          field: fieldPath,
          code: ErrorCodes.REQUIRED_FIELD_MISSING,
          message: `Field '${field}' is required`
        });
        continue;
      }
      
      // Skip further validation if field is not required and empty
      if (!rules.required && (value === undefined || value === null || value === '')) {
        continue;
      }
      
      // Type validation
      if (rules.type && !this.validateType(value, rules.type)) {
        errors.push({
          field: fieldPath,
          code: ErrorCodes.INVALID_INPUT,
          message: `Field '${field}' must be of type ${rules.type}`
        });
      }
      
      // String validations
      if (rules.type === 'string' && typeof value === 'string') {
        if (rules.minLength && value.length < rules.minLength) {
          errors.push({
            field: fieldPath,
            code: ErrorCodes.INVALID_INPUT,
            message: `Field '${field}' must be at least ${rules.minLength} characters`
          });
        }
        
        if (rules.maxLength && value.length > rules.maxLength) {
          errors.push({
            field: fieldPath,
            code: ErrorCodes.INVALID_INPUT,
            message: `Field '${field}' must be no more than ${rules.maxLength} characters`
          });
        }
        
        if (rules.enum && !rules.enum.includes(value)) {
          errors.push({
            field: fieldPath,
            code: ErrorCodes.INVALID_INPUT,
            message: `Field '${field}' must be one of: ${rules.enum.join(', ')}`
          });
        }
      }
      
      // Number validations
      if (rules.type === 'number' && typeof value === 'number') {
        if (rules.min && value < rules.min) {
          errors.push({
            field: fieldPath,
            code: ErrorCodes.INVALID_INPUT,
            message: `Field '${field}' must be at least ${rules.min}`
          });
        }
        
        if (rules.max && value > rules.max) {
          errors.push({
            field: fieldPath,
            code: ErrorCodes.INVALID_INPUT,
            message: `Field '${field}' must be no more than ${rules.max}`
          });
        }
      }
      
      // Array validations
      if (rules.type === 'array' && Array.isArray(value)) {
        if (rules.minLength && value.length < rules.minLength) {
          errors.push({
            field: fieldPath,
            code: ErrorCodes.INVALID_INPUT,
            message: `Field '${field}' must have at least ${rules.minLength} items`
          });
        }
      }
      
      // Date validations
      if (rules.type === 'date') {
        if (!this.isValidDate(value)) {
          errors.push({
            field: fieldPath,
            code: ErrorCodes.INVALID_INPUT,
            message: `Field '${field}' must be a valid date`
          });
        }
      }
    }
    
    return errors;
  }
  
  static validateType(value, type) {
    switch (type) {
      case 'string': return typeof value === 'string';
      case 'number': return typeof value === 'number' && !isNaN(value);
      case 'boolean': return typeof value === 'boolean';
      case 'array': return Array.isArray(value);
      case 'object': return typeof value === 'object' && value !== null && !Array.isArray(value);
      case 'date': return this.isValidDate(value);
      default: return true;
    }
  }
  
  static isValidDate(dateString) {
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  }
  
  static sanitizeInput(data) {
    if (typeof data === 'string') {
      return data.trim().replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    }
    
    if (Array.isArray(data)) {
      return data.map(item => this.sanitizeInput(item));
    }
    
    if (typeof data === 'object' && data !== null) {
      const sanitized = {};
      for (const [key, value] of Object.entries(data)) {
        sanitized[key] = this.sanitizeInput(value);
      }
      return sanitized;
    }
    
    return data;
  }
}

class AuditTrailService {
  static async logAction(action, resourceType, resourceId, details = {}) {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;
      
      const auditEntry = {
        action,
        resourceType,
        resourceId,
        userId: currentUser.uid,
        userEmail: currentUser.email,
        timestamp: serverTimestamp(),
        details,
        ipAddress: await this.getClientIP(),
        userAgent: navigator.userAgent
      };
      
      await addDoc(collection(db, 'auditTrail'), auditEntry);
    } catch (error) {
      devError('Audit trail logging failed:', error);
    }
  }
  
  static async getClientIP() {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch {
      return 'unknown';
    }
  }
}

// =========================
// MAIN SERVICE CLASS
// =========================

class EnhancedFacilitatorService {
  constructor() {
    this.rateLimit = new Map(); // Simple in-memory rate limiting
    this.cache = new Map(); // Simple caching
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }
  
  // =========================
  // AUTHENTICATION & PERMISSIONS
  // =========================
  
  _getCurrentUser() {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new FacilitatorServiceError(
        'User must be authenticated',
        ErrorCodes.AUTH_REQUIRED
      );
    }
    return currentUser;
  }
  
  async _checkPermissions(action, resource) {
    const currentUser = this._getCurrentUser();
    
    // Get user profile to check role
    const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
    if (!userDoc.exists()) {
      throw new FacilitatorServiceError(
        'User profile not found',
        ErrorCodes.RESOURCE_NOT_FOUND
      );
    }
    
    const userData = userDoc.data();
    const userRole = userData.role;
    
    // Basic permission checking (expand based on requirements)
    if (userRole !== 'admin' && userRole !== 'facilitator') {
      throw new FacilitatorServiceError(
        `Insufficient permissions for ${action}`,
        ErrorCodes.INSUFFICIENT_PERMISSIONS
      );
    }
    
    return userData;
  }
  
  _checkRateLimit(userId, action) {
    const key = `${userId}-${action}`;
    const now = Date.now();
    const limit = this.rateLimit.get(key) || { count: 0, resetTime: now + 60000 };
    
    if (now > limit.resetTime) {
      this.rateLimit.set(key, { count: 1, resetTime: now + 60000 });
    } else {
      limit.count++;
      if (limit.count > 100) { // 100 requests per minute
        throw new FacilitatorServiceError(
          'Rate limit exceeded',
          ErrorCodes.RATE_LIMIT_EXCEEDED
        );
      }
      this.rateLimit.set(key, limit);
    }
  }
  
  // =========================
  // CACHING
  // =========================
  
  _getCacheKey(collection, id, params = {}) {
    return `${collection}-${id}-${JSON.stringify(params)}`;
  }
  
  _getFromCache(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }
    return null;
  }
  
  _setCache(key, data) {
    this.cache.set(key, { data, timestamp: Date.now() });
  }
  
  _invalidateCache(pattern) {
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
      }
    }
  }
  
  // =========================
  // FACILITATOR PROGRAM ACCESS
  // =========================

  async getAssignedPrograms() {
    try {
      const currentUser = this._getCurrentUser();

      // Get user data to check role and assigned programs
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (!userDoc.exists()) {
        throw new FacilitatorServiceError(
          'User not found',
          ErrorCodes.RESOURCE_NOT_FOUND
        );
      }

      const userData = userDoc.data();

      // Admin can access all programs, facilitators only assigned ones
      if (userData.role === 'admin') {
        const programsCollection = collection(db, 'programs');
        const snapshot = await getDocs(programsCollection);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }

      if (userData.role === 'facilitator') {
        const assignedProgramIds = userData.assignedPrograms || [];

        if (assignedProgramIds.length === 0) {
          return [];
        }

        // Get only the assigned programs
        const programsCollection = collection(db, 'programs');
        const snapshot = await getDocs(programsCollection);
        const allPrograms = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        return allPrograms.filter(program => assignedProgramIds.includes(program.id));
      }

      throw new FacilitatorServiceError(
        'Only facilitators and admins can access programs',
        ErrorCodes.INSUFFICIENT_PERMISSIONS
      );

    } catch (error) {
      devError('Error getting assigned programs:', error);
      throw error instanceof FacilitatorServiceError ? error : new FacilitatorServiceError(
        'Failed to get assigned programs',
        ErrorCodes.DATABASE_ERROR,
        error.message
      );
    }
  }

  // =========================
  // ENHANCED MODULE MANAGEMENT
  // =========================
  
  async createModule(moduleData) {
    try {
      const currentUser = this._getCurrentUser();
      this._checkRateLimit(currentUser.uid, 'createModule');
      await this._checkPermissions('create', 'module');
      
      // Sanitize and validate input
      const sanitizedData = ValidationUtils.sanitizeInput(moduleData);
      const validationErrors = ValidationUtils.validate(sanitizedData, ValidationSchemas.module);
      
      if (validationErrors.length > 0) {
        throw new FacilitatorServiceError(
          'Module validation failed',
          ErrorCodes.VALIDATION_FAILED,
          validationErrors
        );
      }
      
      // Validate date logic
      if (new Date(sanitizedData.startDate) >= new Date(sanitizedData.endDate)) {
        throw new FacilitatorServiceError(
          'End date must be after start date',
          ErrorCodes.INVALID_INPUT
        );
      }

      // Check if facilitator is assigned to the specified program
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (!userDoc.exists()) {
        throw new FacilitatorServiceError(
          'User not found',
          ErrorCodes.RESOURCE_NOT_FOUND
        );
      }

      const userData = userDoc.data();
      const assignedPrograms = userData.assignedPrograms || [];

      // Admin can create modules for any program, facilitators only for assigned ones
      if (userData.role === 'facilitator' && !assignedPrograms.includes(sanitizedData.programId)) {
        throw new FacilitatorServiceError(
          'You are not authorized to create modules for this program. Contact an administrator to get assigned to this program.',
          ErrorCodes.INSUFFICIENT_PERMISSIONS,
          { programId: sanitizedData.programId, assignedPrograms: assignedPrograms }
        );
      }
      
      // Create enhanced module structure
      const module = {
        // Basic Information
        id: `module_${Date.now()}`,
        moduleName: sanitizedData.moduleName,
        description: sanitizedData.description,
        programId: sanitizedData.programId,
        cohortIds: Array.isArray(sanitizedData.cohortIds) ? sanitizedData.cohortIds : [],
        cohortId: Array.isArray(sanitizedData.cohortIds) && sanitizedData.cohortIds.length === 1
          ? sanitizedData.cohortIds[0]
          : null,
        version: '1.0',
        language: 'en',

        // Classification
        category: 'foundational',
        difficulty: sanitizedData.difficulty,
        estimatedHours: sanitizedData.estimatedHours,
        creditHours: Math.ceil(sanitizedData.estimatedHours / 10),
        skillsLevel: 'NQF Level 2',
        
        // Scheduling
        startDate: sanitizedData.startDate,
        endDate: sanitizedData.endDate,
        flexibleScheduling: true,
        selfPaced: false,
        
        // Prerequisites and Dependencies
        prerequisites: sanitizedData.prerequisites || [],
        corequisites: [],
        followUpModules: [],
        
        // Learning Objectives
        learningObjectives: sanitizedData.learningObjectives,
        
        // Content Structure
        lessons: [],
        
        // Assessment Strategy
        assessmentStrategy: {
          formative: ['weekly_quizzes', 'practical_exercises'],
          summative: ['final_project', 'competency_assessment'],
          weightings: {
            participation: 20,
            assignments: 40,
            finalAssessment: 40
          }
        },
        
        // Analytics and Performance (initialized)
        analytics: {
          enrollmentCount: 0,
          completionRate: 0,
          averageScore: 0,
          satisfactionRating: 0,
          timeToComplete: 0,
          dropoutRate: 0
        },
        
        // Administrative
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
        status: 'draft',
        
        // Compliance and Accreditation
        accreditation: {
          provider: 'SETA Accredited',
          certificateTemplate: 'template_digital_literacy',
          cpd_points: Math.ceil(sanitizedData.estimatedHours / 15),
          validUntil: '2026-12-31'
        }
      };
      
      const docRef = await addDoc(collection(db, 'modules'), module);
      
      // Log audit trail
      await AuditTrailService.logAction('module_created', 'module', docRef.id, {
        moduleName: module.moduleName,
        difficulty: module.difficulty,
        estimatedHours: module.estimatedHours
      });
      
      // Invalidate cache
      this._invalidateCache('modules');
      
      devLog('Enhanced module created with ID:', docRef.id);
      return docRef.id;
      
    } catch (error) {
      if (error instanceof FacilitatorServiceError) {
        throw error;
      }
      
      devError('Error creating module:', error);
      throw new FacilitatorServiceError(
        'Failed to create module',
        ErrorCodes.FIREBASE_ERROR,
        error.message
      );
    }
  }

  async updateModule(moduleId, moduleData) {
    try {
      const currentUser = this._getCurrentUser();
      this._checkRateLimit(currentUser.uid, 'updateModule');
      await this._checkPermissions('edit', 'module');

      if (!moduleId) {
        throw new FacilitatorServiceError(
          'Module ID is required',
          ErrorCodes.INVALID_INPUT
        );
      }

      const sanitizedData = ValidationUtils.sanitizeInput(moduleData);
      const validationErrors = ValidationUtils.validate(sanitizedData, ValidationSchemas.module);

      if (validationErrors.length > 0) {
        throw new FacilitatorServiceError(
          'Module validation failed',
          ErrorCodes.VALIDATION_FAILED,
          validationErrors
        );
      }

      if (new Date(sanitizedData.startDate) >= new Date(sanitizedData.endDate)) {
        throw new FacilitatorServiceError(
          'End date must be after start date',
          ErrorCodes.INVALID_INPUT
        );
      }

      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (!userDoc.exists()) {
        throw new FacilitatorServiceError(
          'User not found',
          ErrorCodes.RESOURCE_NOT_FOUND
        );
      }

      const userData = userDoc.data();
      const assignedPrograms = userData.assignedPrograms || [];
      if (userData.role === 'facilitator' && !assignedPrograms.includes(sanitizedData.programId)) {
        throw new FacilitatorServiceError(
          'You are not authorized to edit modules for this program. Contact an administrator.',
          ErrorCodes.INSUFFICIENT_PERMISSIONS,
          { programId: sanitizedData.programId, assignedPrograms: assignedPrograms }
        );
      }

      const moduleRef = doc(db, 'modules', moduleId);
      await updateDoc(moduleRef, {
        moduleName: sanitizedData.moduleName,
        description: sanitizedData.description,
        programId: sanitizedData.programId,
        cohortIds: Array.isArray(sanitizedData.cohortIds) ? sanitizedData.cohortIds : [],
        cohortId: Array.isArray(sanitizedData.cohortIds) && sanitizedData.cohortIds.length === 1
          ? sanitizedData.cohortIds[0]
          : null,
        difficulty: sanitizedData.difficulty,
        estimatedHours: sanitizedData.estimatedHours,
        startDate: sanitizedData.startDate,
        endDate: sanitizedData.endDate,
        learningObjectives: sanitizedData.learningObjectives,
        prerequisites: sanitizedData.prerequisites || [],
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid
      });

      await AuditTrailService.logAction('module_updated', 'module', moduleId, {
        moduleName: sanitizedData.moduleName,
        difficulty: sanitizedData.difficulty,
        estimatedHours: sanitizedData.estimatedHours
      });

      this._invalidateCache('modules');

      return true;
    } catch (error) {
      if (error instanceof FacilitatorServiceError) {
        throw error;
      }

      devError('Error updating module:', error);
      throw new FacilitatorServiceError(
        'Failed to update module',
        ErrorCodes.FIREBASE_ERROR,
        error.message
      );
    }
  }
  
  async getModules(creatorId = null, options = {}) {
    try {
      const currentUser = this._getCurrentUser();
      this._checkRateLimit(currentUser.uid, 'getModules');
      
      const { page = 1, pageSize = 20, status = null, difficulty = null } = options;
      
      // Check cache
      const cacheKey = this._getCacheKey('modules', creatorId || 'all', options);
      const cached = this._getFromCache(cacheKey);
      if (cached) return cached;
      
      let q = collection(db, 'modules');
      const constraints = [];
      
      // Add filters
      if (creatorId) {
        constraints.push(where('createdBy', '==', creatorId));
      }
      
      if (status) {
        constraints.push(where('status', '==', status));
      }
      
      if (difficulty) {
        constraints.push(where('difficulty', '==', difficulty));
      }
      
      // Add ordering and pagination
      constraints.push(orderBy('createdAt', 'desc'));
      
      if (page > 1) {
        // For pagination, you'd need to implement startAfter with the last document
        constraints.push(limit(pageSize));
      } else {
        constraints.push(limit(pageSize));
      }
      
      q = query(q, ...constraints);
      
      const querySnapshot = await getDocs(q);
      let modules = [];
      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        modules.push({
          ...data,
          id: docSnap.id,
          moduleCode: data.id || null
        });
      });

      // Filter modules by assigned programs for facilitators
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const assignedPrograms = userData.assignedPrograms || [];

        if (userData.role === 'facilitator') {
          modules = modules.filter(module =>
            module.programId && assignedPrograms.includes(module.programId)
          );
        }
        // Admins can see all modules, so no filtering needed
      }

      // Cache results
      this._setCache(cacheKey, modules);

      return modules;
      
    } catch (error) {
      devError('Error fetching modules:', error);
      throw new FacilitatorServiceError(
        'Failed to fetch modules',
        ErrorCodes.FIREBASE_ERROR,
        error.message
      );
    }
  }
  
  // =========================
  // ENHANCED ASSIGNMENT MANAGEMENT
  // =========================
  
  async createAssignment(assignmentData) {
    try {
      const currentUser = this._getCurrentUser();
      this._checkRateLimit(currentUser.uid, 'createAssignment');
      await this._checkPermissions('create', 'assignment');
      
      // Sanitize and validate input
      const sanitizedData = ValidationUtils.sanitizeInput(assignmentData);
      const validationErrors = ValidationUtils.validate(sanitizedData, ValidationSchemas.assignment);
      
      if (validationErrors.length > 0) {
        throw new FacilitatorServiceError(
          'Assignment validation failed',
          ErrorCodes.VALIDATION_FAILED,
          validationErrors
        );
      }
      
      // Check if module exists and if facilitator has access to it
      const moduleDoc = await getDoc(doc(db, 'modules', sanitizedData.moduleId));
      if (!moduleDoc.exists()) {
        throw new FacilitatorServiceError(
          'Module not found',
          ErrorCodes.RESOURCE_NOT_FOUND
        );
      }

      const moduleData = moduleDoc.data();

      // Check if facilitator has access to the module's program
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (!userDoc.exists()) {
        throw new FacilitatorServiceError(
          'User not found',
          ErrorCodes.RESOURCE_NOT_FOUND
        );
      }

      const userData = userDoc.data();
      const assignedPrograms = userData.assignedPrograms || [];

      // Admin can create assignments for any module, facilitators only for assigned programs
      if (userData.role === 'facilitator' && moduleData.programId && !assignedPrograms.includes(moduleData.programId)) {
        throw new FacilitatorServiceError(
          'You are not authorized to create assignments for this module. Contact an administrator to get assigned to this program.',
          ErrorCodes.INSUFFICIENT_PERMISSIONS,
          { moduleId: sanitizedData.moduleId, programId: moduleData.programId, assignedPrograms: assignedPrograms }
        );
      }
      
      // Create enhanced assignment structure
      const assignment = {
        // Basic Information
        id: `assignment_${Date.now()}`,
        title: sanitizedData.title,
        description: sanitizedData.description || '',
        moduleId: sanitizedData.moduleId,
        cohortId: sanitizedData.cohortId || null,
        
        // Classification
        type: sanitizedData.type,
        category: 'formative',
        priority: sanitizedData.priority || 'medium',
        complexity: 'intermediate',
        
        // Timing and Deadlines
        assignedDate: serverTimestamp(),
        dueDate: sanitizedData.dueDate,
        submissionDeadline: sanitizedData.dueDate,
        lateSubmissionAllowed: true,
        lateSubmissionPenalty: 10,
        maxLateDays: 3,
        
        // Effort and Scoring
        estimatedHours: sanitizedData.estimatedHours || 2,
        maxScore: sanitizedData.maxScore,
        passingScore: Math.floor(sanitizedData.maxScore * 0.6),
        weightingInModule: 25,
        
        // Instructions and Requirements
        instructions: sanitizedData.instructions || '',
        detailedRequirements: [],
        
        // Submission Format and Method
        submissionFormat: sanitizedData.submissionFormat || 'online',
        submissionMethods: ['file_upload'],
        fileFormats: ['.pdf', '.docx'],
        maxFileSize: 10,
        maxFiles: 3,
        
        // Assessment Rubric
        rubric: sanitizedData.rubric || [],
        
        // Attempts and Retakes
        maxAttempts: 2,
        retakePolicy: 'best_score',
        retakeDelay: 24,
        
        // Tracking and Analytics
        submissions: {},
        
        // Administrative
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
        status: 'active',
        
        // Analytics (initialized)
        analytics: {
          submissionRate: 0,
          averageScore: 0,
          completionTime: 0,
          difficultyRating: 0,
          clarificationRequests: 0
        }
      };
      
      const docRef = await addDoc(collection(db, 'assignments'), assignment);
      
      // Log audit trail
      await AuditTrailService.logAction('assignment_created', 'assignment', docRef.id, {
        title: assignment.title,
        moduleId: assignment.moduleId,
        maxScore: assignment.maxScore
      });
      
      // Invalidate cache
      this._invalidateCache('assignments');
      
      devLog('Enhanced assignment created with ID:', docRef.id);
      return docRef.id;
      
    } catch (error) {
      if (error instanceof FacilitatorServiceError) {
        throw error;
      }
      
      devError('Error creating assignment:', error);
      throw new FacilitatorServiceError(
        'Failed to create assignment',
        ErrorCodes.FIREBASE_ERROR,
        error.message
      );
    }
  }
  
  // =========================
  // ENHANCED COHORT MANAGEMENT
  // =========================
  
  async createCohort(cohortData) {
    try {
      const currentUser = this._getCurrentUser();
      this._checkRateLimit(currentUser.uid, 'createCohort');
      await this._checkPermissions('create', 'cohort');
      
      // Sanitize and validate input
      const sanitizedData = ValidationUtils.sanitizeInput(cohortData);
      const validationErrors = ValidationUtils.validate(sanitizedData, ValidationSchemas.cohort);
      
      if (validationErrors.length > 0) {
        throw new FacilitatorServiceError(
          'Cohort validation failed',
          ErrorCodes.VALIDATION_FAILED,
          validationErrors
        );
      }
      
      // Create enhanced cohort structure
      const cohort = {
        // Basic Information
        id: `cohort_${Date.now()}`,
        name: sanitizedData.name,
        description: sanitizedData.description || '',
        programId: sanitizedData.programId,
        
        // Dates and Status
        startDate: sanitizedData.startDate,
        endDate: sanitizedData.endDate,
        enrollmentDeadline: sanitizedData.enrollmentDeadline || sanitizedData.startDate,
        status: 'draft',
        
        // Personnel
        facilitators: [currentUser.uid],
        primaryFacilitator: currentUser.uid,
        learners: [],
        maxLearners: sanitizedData.maxLearners,
        currentEnrollment: 0,
        
        // Curriculum
        modules: sanitizedData.modules || [],
        requiredModules: [],
        electiveModules: [],
        
        // Scheduling
        schedule: {
          sessionsPerWeek: 3,
          sessionDuration: 2.5,
          preferredDays: ['monday', 'wednesday', 'friday'],
          preferredTime: '09:00',
          timezone: 'Africa/Johannesburg'
        },
        
        // Performance Tracking
        targets: {
          completionRate: 85,
          attendanceRate: 80,
          employmentRate: 70
        },
        
        // Administrative
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
        
        // Metadata
        tags: [],
        budget: {
          allocated: 0,
          spent: 0,
          currency: 'ZAR'
        }
      };
      
      const docRef = await addDoc(collection(db, 'cohorts'), cohort);
      
      // Update user's managedCohorts array
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        managedCohorts: arrayUnion(docRef.id),
        updatedAt: serverTimestamp()
      });
      
      // Log audit trail
      await AuditTrailService.logAction('cohort_created', 'cohort', docRef.id, {
        name: cohort.name,
        maxLearners: cohort.maxLearners,
        programId: cohort.programId
      });
      
      // Invalidate cache
      this._invalidateCache('cohorts');
      
      devLog('Enhanced cohort created with ID:', docRef.id);
      return docRef.id;
      
    } catch (error) {
      if (error instanceof FacilitatorServiceError) {
        throw error;
      }
      
      devError('Error creating cohort:', error);
      throw new FacilitatorServiceError(
        'Failed to create cohort',
        ErrorCodes.FIREBASE_ERROR,
        error.message
      );
    }
  }
  
  // =========================
  // ENHANCED PROGRESS REPORTING
  // =========================
  
  async createProgressReport(reportData) {
    try {
      const currentUser = this._getCurrentUser();
      this._checkRateLimit(currentUser.uid, 'createProgressReport');
      await this._checkPermissions('create', 'progressReport');
      
      // Sanitize and validate input
      const sanitizedData = ValidationUtils.sanitizeInput(reportData);
      const validationErrors = ValidationUtils.validate(sanitizedData, ValidationSchemas.progressReport);
      
      if (validationErrors.length > 0) {
        throw new FacilitatorServiceError(
          'Progress report validation failed',
          ErrorCodes.VALIDATION_FAILED,
          validationErrors
        );
      }
      
      // Create enhanced progress report structure
      const report = {
        // Basic Information
        id: `report_${Date.now()}`,
        reportType: 'session_report',
        title: sanitizedData.title || `Session Report - ${new Date(sanitizedData.sessionDate).toLocaleDateString()}`,
        
        // Context and Scope
        cohortId: sanitizedData.cohortId,
        moduleId: sanitizedData.moduleId || null,
        sessionId: sanitizedData.sessionId || null,
        facilitatorId: currentUser.uid,
        
        // Date and Time
        sessionDate: sanitizedData.sessionDate,
        reportGeneratedAt: serverTimestamp(),
        
        // Attendance Analytics
        attendance: sanitizedData.attendance,
        
        // Session Content and Activities
        contentCovered: sanitizedData.contentCovered || [],
        
        // Highlights and Challenges
        highlights: sanitizedData.highlights ? [sanitizedData.highlights] : [],
        challenges: sanitizedData.challenges || [],
        
        // Next Session Planning
        nextSessionPreparation: {
          topicsForReview: [],
          prerequisiteCheck: true,
          materialsNeeded: [],
          anticipatedChallenges: [],
          preparationTime: 2,
          resourcesRequired: []
        },
        
        // Administrative
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        status: 'draft'
      };
      
      const docRef = await addDoc(collection(db, 'progressReports'), report);
      
      // Log audit trail
      await AuditTrailService.logAction('progress_report_created', 'progressReport', docRef.id, {
        cohortId: report.cohortId,
        sessionDate: report.sessionDate,
        attendanceRate: report.attendance.attendanceRate
      });
      
      // Invalidate cache
      this._invalidateCache('progressReports');
      
      devLog('Enhanced progress report created with ID:', docRef.id);
      return docRef.id;
      
    } catch (error) {
      if (error instanceof FacilitatorServiceError) {
        throw error;
      }
      
      devError('Error creating progress report:', error);
      throw new FacilitatorServiceError(
        'Failed to create progress report',
        ErrorCodes.FIREBASE_ERROR,
        error.message
      );
    }
  }
  
  // =========================
  // UTILITY FUNCTIONS
  // =========================
  
  generateTeamsLink() {
    const meetingId = Math.random().toString(36).substring(2, 15);
    return `https://teams.microsoft.com/l/meetup-join/19%3ameeting_${meetingId}`;
  }
  
  calculateNotesReleaseTime(sessionDate, sessionTime, delayMinutes = 5) {
    const sessionDateTime = new Date(`${sessionDate}T${sessionTime}`);
    const releaseTime = new Date(sessionDateTime.getTime() + (delayMinutes * 60000));
    return releaseTime.toISOString();
  }
  
  // =========================
  // ERROR RECOVERY
  // =========================
  
  async retryOperation(operation, maxRetries = 3, backoffMs = 1000) {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        if (attempt === maxRetries || error instanceof FacilitatorServiceError) {
          throw error;
        }
        
        devLog(`Operation failed (attempt ${attempt}/${maxRetries}):`, error.message);
        await new Promise(resolve => setTimeout(resolve, backoffMs * attempt));
      }
    }
  }
  
  // =========================
  // HEALTH CHECK
  // =========================
  
  async healthCheck() {
    try {
      const testDoc = doc(db, 'system', 'healthcheck');
      await getDoc(testDoc);
      return { status: 'healthy', timestamp: new Date().toISOString() };
    } catch (error) {
      return { status: 'unhealthy', error: error.message, timestamp: new Date().toISOString() };
    }
  }
}

// Create and export singleton instance
const enhancedFacilitatorService = new EnhancedFacilitatorService();
export default enhancedFacilitatorService;
export { FacilitatorServiceError, ErrorCodes, ValidationUtils, AuditTrailService };
